import React, { useCallback, useEffect, useState } from 'react';
import styled from "styled-components";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import TextField from '@shared/ui/atoms/TextField';
import { agent } from "@shared/services/agent";
import { useDispatch } from "react-redux";
import { failureNotification } from "@shared/store/notification.duck";
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { CheckinIpAddressStatus, CheckinIpStatusPill, IpValue } from './CheckinIpStatus';

interface NetworkMapping {
  id: number;
  networkName: string;
  officeId: number;
}

interface CheckinIpAddress {
  id: number;
  ipAddress: string;
  status: CheckinIpAddressStatus;
}

export function AutomaticCheckinPanel(props: Props) {
  const { office } = props;
  const [networkMappings, setNetworkMappings] = useState<NetworkMapping[]>([]);
  const [newNetworkName, setNewNetworkName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [ipAddresses, setIpAddresses] = useState<CheckinIpAddress[]>([]);
  const [newIpAddress, setNewIpAddress] = useState('');
  const [isSavingIp, setIsSavingIp] = useState(false);
  const dispatch = useDispatch();

  const fetchMappings = useCallback(async () => {
    try {
      const data = await agent.requests.get(`/api/office/${office.id}/network-mappings`);
      setNetworkMappings(data);
    } catch {
      // Silently fail on load
    }
  }, [office.id]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const fetchIpAddresses = useCallback(async () => {
    try {
      const data = await agent.requests.get(`/api/office/${office.id}/checkin-ip-addresses`);
      setIpAddresses(data);
    } catch {
      // Silently fail on load
    }
  }, [office.id]);

  useEffect(() => {
    fetchIpAddresses();
  }, [fetchIpAddresses]);

  const handleAddIp = async () => {
    const trimmed = newIpAddress.trim();
    if (!trimmed) return;

    try {
      setIsSavingIp(true);
      const saved = await agent.requests.post(`/api/office/${office.id}/checkin-ip-addresses`, { ipAddress: trimmed });
      setIpAddresses(prev => [...prev, saved]);
      setNewIpAddress('');
    } catch (err: any) {
      const message = err?.response?.body?.message || 'Could not add this IP address';
      dispatch(failureNotification(message));
    } finally {
      setIsSavingIp(false);
    }
  };

  const handleDeleteIp = async (row: CheckinIpAddress) => {
    if (!window.confirm(`Remove ${row.ipAddress}? Check-ins from this address will stop being accepted.`)) return;
    try {
      await agent.requests.del(`/api/office/${office.id}/checkin-ip-addresses/${row.id}`);
      setIpAddresses(prev => prev.filter(r => r.id !== row.id));
    } catch {
      dispatch(failureNotification('Failed to remove IP address'));
    }
  };

  const handleAdd = async () => {
    const trimmed = newNetworkName.trim();
    if (!trimmed) return;

    try {
      setIsSaving(true);
      const saved = await agent.requests.post(`/api/office/${office.id}/network-mappings`, { networkName: trimmed });
      setNetworkMappings(prev => [...prev, saved]);
      setNewNetworkName('');
    } catch (err: any) {
      const message = err?.response?.body?.message || 'This network name is already mapped to an office';
      dispatch(failureNotification(message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await agent.requests.del(`/api/office/${office.id}/network-mappings/${id}`);
      setNetworkMappings(prev => prev.filter(m => m.id !== id));
    } catch {
      dispatch(failureNotification('Failed to remove network mapping'));
    }
  };

  return (
    <Container>
      <HeadlineMedium style={{marginBottom: 24}}>Automatic Check-in</HeadlineMedium>
      <FlatContentCard style={{marginBottom: 32}}>
        <BodyRegular weight={600}>How it works</BodyRegular>
        <BodyRegular>Map Wi-Fi network names to this office, and add the office's internet address. When a device connects to a mapped network from an approved address, the user is automatically checked in.</BodyRegular>
      </FlatContentCard>

      <BodyRegular weight={600} style={{marginBottom: 12}}>Network Names</BodyRegular>

      {networkMappings.map(mapping => (
        <MappingRow key={mapping.id}>
          <BodyRegular weight={500}>{mapping.networkName}</BodyRegular>
          <Icon icon={IconTypes.Close} color="grey" onClick={() => handleDelete(mapping.id)} />
        </MappingRow>
      ))}

      {networkMappings.length === 0 && (
        <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>No network names mapped to this office yet.</BodyRegular>
      )}

      <AddRow>
        <TextField value={newNetworkName}
                   onChange={setNewNetworkName}
                   placeholder="textinput.network-name-placeholder"
                   style={{flex: 1, marginBottom: 0, marginRight: 12}} />
        <PrimaryButton text="Add" click={handleAdd} disabled={isSaving || !newNetworkName.trim()} size={ButtonSize.Small} />
      </AddRow>

      <BodyRegular weight={600} style={{marginTop: 32, marginBottom: 4}}>Office internet addresses</BodyRegular>
      <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>
        Check-ins are only accepted from your office's public IPv4 address, the one websites see. Add one address at a time.
        Team Today checks each address belongs to you or your internet provider before approving it.
      </BodyRegular>

      {ipAddresses.map(row => (
        <MappingRow key={row.id}>
          <IpRowMain>
            <IpValue>{row.ipAddress}</IpValue>
            <CheckinIpStatusPill status={row.status} />
          </IpRowMain>
          <Icon icon={IconTypes.Close} color="grey" onClick={() => handleDeleteIp(row)} />
        </MappingRow>
      ))}

      {ipAddresses.some(row => row.status === 'REJECTED') && (
        <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>
          An address that is not approved is never used. Email support@team-today.com if you think this is a mistake.
        </BodyRegular>
      )}

      {ipAddresses.length === 0 && (
        <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>No IP addresses added for this office yet.</BodyRegular>
      )}

      <AddRow>
        <TextField value={newIpAddress}
                   onChange={setNewIpAddress}
                   placeholder="textinput.ip-address-placeholder"
                   style={{flex: 1, marginBottom: 0, marginRight: 12}} />
        <PrimaryButton text="Add" click={handleAddIp} disabled={isSavingIp || !newIpAddress.trim()} size={ButtonSize.Small} />
      </AddRow>
    </Container>
  );
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div``

const MappingRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: ${Colours.veryLightGrey};
  border-radius: 8px;
  margin-bottom: 8px;
`

const AddRow = styled(Row)`
  align-items: flex-end;
  margin-top: 12px;
`

const IpRowMain = styled(Row)`
  align-items: center;
  gap: 12px;
`
